import { useMemo } from 'react';

import type { ProjectArtifactEntry } from '../lib/api/types';
import { useProjectArtifactsManifest } from '../lib/api/useProjectArtifactsManifest';
import type { ProjectArtifact } from '../types';

const formatUserName = (
  firstName: string | undefined,
  lastName: string | undefined,
  fallback: string | undefined
): string => {
  const full = [firstName, lastName].filter(Boolean).join(' ').trim();
  return full || fallback?.trim() || 'Unknown';
};

const sortByUpdatedAtDesc = (a: ProjectArtifact, b: ProjectArtifact): number => {
  if (a.updatedAt === b.updatedAt) return 0;
  return a.updatedAt < b.updatedAt ? 1 : -1;
};

export interface UseProjectArtifactsResult {
  artifacts: ProjectArtifact[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Map a typed manifest entry to the unified ``ProjectArtifact`` view
 * model. Narrows on ``entry.kind`` so the kind-specific metadata
 * sidecar lands on ``kindSpecific`` without a re-classification step.
 */
const manifestEntryToArtifact = (entry: ProjectArtifactEntry): ProjectArtifact | null => {
  switch (entry.kind) {
    case 'cohort': {
      // The enriched manifest carries the creator's display name (cohort_profile
      // stores it) on the row, plus the action-menu scalars in metadata.
      const meta = entry.metadata;
      return {
        id: entry.id,
        kind: 'cohort',
        name: entry.name,
        status: entry.status ?? '',
        assetStatus: entry.assetStatus ?? null,
        // OR-6615: scope + relationship ride through additively; a pre-flag
        // backend omits them, so default scope null / relationship 'created'.
        scope: entry.scope ?? null,
        relationship: entry.relationship ?? 'created',
        owner: {
          id: meta?.createdByUserId ?? entry.attachedByUserId,
          name: formatUserName(
            entry.attachedByUserGivenName ?? undefined,
            entry.attachedByUserFamilyName ?? undefined,
            meta?.createdByUserId ?? entry.attachedByUserId
          ),
        },
        updatedAt: entry.updatedAt ?? entry.attachedAt ?? '',
        datasetReleaseName: entry.datasetReleaseName ?? null,
        kindSpecific: {
          // Scalars the row action menu (`CohortsListActionMenu`) needs —
          // built into a partial Cohort by `ProjectArtifactRowActions`.
          cohortProfileId: entry.id,
          latestCohortVersionId: meta?.latestCohortVersionId ?? null,
          latestSubjectCount: meta?.latestSubjectCount ?? null,
          isExportInProgress: meta?.isExportInProgress ?? false,
          createdByUserId: meta?.createdByUserId ?? null,
        },
      };
    }
    case 'av': {
      const source = entry.metadata?.source ?? null;
      return {
        id: entry.id,
        kind: 'variable',
        name: entry.name,
        // Display label = data_element, falling back to the internal slug when
        // the AV has no data_element. ``name`` stays the slug for search/sort.
        displayName: entry.metadata?.dataElement ?? entry.name,
        status: entry.status ?? 'Variable',
        assetStatus: entry.assetStatus ?? null,
        // OR-6615: scope + relationship ride through additively; a pre-flag
        // backend omits them, so default scope null / relationship 'created'.
        scope: entry.scope ?? null,
        relationship: entry.relationship ?? 'created',
        owner: {
          id: entry.attachedByUserId,
          name: formatUserName(
            entry.attachedByUserGivenName ?? undefined,
            entry.attachedByUserFamilyName ?? undefined,
            entry.attachedByUserId
          ),
        },
        updatedAt: entry.updatedAt ?? entry.attachedAt ?? '',
        datasetReleaseName: entry.datasetReleaseName ?? null,
        kindSpecific: {
          description: entry.description ?? undefined,
          // Backing-store discriminator — downstream callers (e.g. the
          // AV detail page) use this to pick the right detail endpoint
          // and the central status resolver branches on it.
          source,
          // OR-6090: rename authz — the row action menu gates Rename on
          // this, not on `owner.id` above (which is who attached the
          // variable to this project, not who created it).
          createdByUserId: entry.metadata?.createdByUserId ?? null,
        },
      };
    }
    case 'concept_group':
      return {
        id: entry.id,
        kind: 'concept_group',
        name: entry.name,
        status: entry.status ?? '',
        assetStatus: entry.assetStatus ?? null,
        // OR-6615: scope + relationship ride through additively; a pre-flag
        // backend omits them, so default scope null / relationship 'created'.
        scope: entry.scope ?? null,
        relationship: entry.relationship ?? 'created',
        owner: {
          id: entry.attachedByUserId,
          name: formatUserName(
            entry.attachedByUserGivenName ?? undefined,
            entry.attachedByUserFamilyName ?? undefined,
            entry.attachedByUserId
          ),
        },
        updatedAt: entry.updatedAt ?? entry.attachedAt ?? '',
        datasetReleaseName: entry.datasetReleaseName ?? null,
        kindSpecific: {
          description: entry.description ?? undefined,
          // Delete-permission gating pair (Verantos-default / same-org),
          // consumed by `ConceptGroupRowActions` — mirrors the org-wide
          // library's `isVerantosDefault`/`canDelete` check.
          organizationId: entry.metadata?.organizationId ?? null,
          owner: entry.metadata?.owner ?? null,
        },
      };
    case 'analytical_dataset':
      return {
        id: entry.id,
        kind: 'analytical_dataset',
        name: entry.name,
        status: entry.status ?? '',
        assetStatus: entry.assetStatus ?? null,
        // OR-6615: scope + relationship ride through additively; a pre-flag
        // backend omits them, so default scope null / relationship 'created'.
        scope: entry.scope ?? null,
        relationship: entry.relationship ?? 'created',
        owner: {
          id: entry.attachedByUserId,
          // The AD profile stores the creator's name, so resolve a display
          // name (unlike av / concept_group, which only carry the user id).
          name: formatUserName(
            entry.attachedByUserGivenName ?? undefined,
            entry.attachedByUserFamilyName ?? undefined,
            entry.attachedByUserId
          ),
        },
        updatedAt: entry.updatedAt ?? entry.attachedAt ?? '',
        datasetReleaseName: entry.datasetReleaseName ?? null,
        kindSpecific: {
          description: entry.description ?? undefined,
          // Direct-link nav: skip the version resolver when the manifest already
          // carries the latest version id (mirrors cohort's latestCohortVersionId).
          latestAnalyticalDatasetVersionId:
            entry.metadata?.latestAnalyticalDatasetVersionId ?? null,
        },
      };
    case 'analysis':
      return {
        id: entry.id,
        kind: 'analysis',
        name: entry.name,
        status: entry.status ?? '',
        assetStatus: entry.assetStatus ?? null,
        // OR-6615: scope + relationship ride through additively; a pre-flag
        // backend omits them, so default scope null / relationship 'created'.
        scope: entry.scope ?? null,
        relationship: entry.relationship ?? 'created',
        owner: {
          id: entry.attachedByUserId,
          name: formatUserName(
            entry.attachedByUserGivenName ?? undefined,
            entry.attachedByUserFamilyName ?? undefined,
            entry.attachedByUserId
          ),
        },
        updatedAt: entry.updatedAt ?? entry.attachedAt ?? '',
        // An analysis has no single dataset of its own (it inherits a study
        // period from its cohort source(s) instead) — always null, unlike
        // cohort/AD.
        datasetReleaseName: null,
        kindSpecific: {
          description: entry.description ?? undefined,
          // Direct-link nav: skip the version resolver when the manifest already
          // carries the latest version id (mirrors the AD row).
          latestAnalysisVersionId: entry.metadata?.latestAnalysisVersionId ?? null,
        },
      };
  }
};

/**
 * Project artifact list — sourced entirely from the project artifact
 * manifest (`GET /projects/:pid/artifacts`).
 *
 * The BE merges materialized ``PROJECT_ARTIFACT`` rows with read-side
 * backfill for cohorts not yet in the index, attaches kind-specific
 * typed metadata, and orders by ``updatedAt`` desc. The FE just maps
 * each entry to the view-model.
 *
 * Going forward every artifact (cohort, AV, concept group, future
 * analytical dataset) materializes into ``PROJECT_ARTIFACT`` at create
 * time; the manifest is the single source of truth. Per-kind list
 * queries (``useCohortListQuery``, ``useAnalyticalVariableListQuery``)
 * remain available for the standalone list pages but are no longer
 * fanned out here.
 *
 * Concept groups currently appear once a project actually pins one
 * through ``PROJECT_ARTIFACT`` — no derivation from cohort / AV IRs.
 * Analytical datasets have no canonical entity in the platform yet;
 * the kind is scaffolding. Analysis is the same: the manifest
 * doesn't join analysis-profile rows yet, so this mapping is ready for
 * when it does.
 */
export const useProjectArtifacts = (projectId: string): UseProjectArtifactsResult => {
  const manifest = useProjectArtifactsManifest(projectId);

  const artifacts = useMemo<ProjectArtifact[]>(() => {
    const mapped: ProjectArtifact[] = [];
    manifest.entries.forEach((entry) => {
      const artifact = manifestEntryToArtifact(entry);
      if (artifact) mapped.push(artifact);
    });
    return mapped.sort(sortByUpdatedAtDesc);
  }, [manifest.entries]);

  return {
    artifacts,
    isLoading: manifest.isLoading,
    isError: manifest.isError,
  };
};
